import React, { FC, useCallback, useEffect } from 'react';
import { Redirect } from 'react-router-dom';

import { EvError, EvFlexColumn, EvSpinner, TOAST_STATUS } from '@evinced-private/ui-common';

import { LABEL_MANAGEMENT_PAGE_DAID } from 'data-automation-ids/LabelManagementAutomationIds';
import { useLabelManagementStore } from 'src/apps/dashboard/stores/labelManagementStore';
import { RoutesHelper } from 'src/common/helpers/RoutesHelper';
import { ProductType } from 'src/common/interfaces/Tenant';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { useNotifications } from 'src/common/providers/notificationsProvider/NotificationsConsumer';
import { useUserTenant } from 'src/common/providers/userTenantProvider/UserTenantProvider';

import { PAGE_HEADER_LABELS } from './components/page-header/LabelManagementPageHeader.labels';
import {
	DeleteLabelConfirmModal,
	LabelManagementPageHeader,
	LabelPanel,
	LabelsTable
} from './components';
import { PAGE_LABELS } from './LabelManagementPage.labels';

export const LabelManagementPage: FC = () => {
	const { getProductIdByType, hasAdminPermissions } = useUserTenant();
	const { configuration } = useConfiguration();
	const { toast } = useNotifications();
	const {
		labels,
		isLoading,
		error,
		isPanelOpen,
		panelMode,
		activeLabel,
		isSaving,
		isDeleting,
		labelToDelete,
		deleteError,
		fetchLabels,
		saveLabel,
		deleteLabel,
		openAddPanel,
		openEditPanel,
		closePanel,
		requestDelete,
		cancelDelete,
		reset
	} = useLabelManagementStore();

	const productId = getProductIdByType(ProductType.PLATFORM);

	useEffect(() => {
		if (productId) {
			fetchLabels(productId);
		}
	}, [productId, fetchLabels]);

	useEffect(() => {
		return () => {
			reset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleConfirmDelete = useCallback(async () => {
		if (!productId) {
			return;
		}
		const deleted = await deleteLabel(productId);
		if (deleted) {
			toast?.({
				show: true,
				status: TOAST_STATUS.SUCCESS,
				announcement: PAGE_LABELS.DELETE_LABEL_SUCCESS
			});
		}
	}, [productId, deleteLabel, toast]);

	const isLabelsManagementEnabled =
		configuration?.PLATFORM?.DEV_TOGGLES?.ENABLE_LABELS_MANAGEMENT ?? false;

	if (!hasAdminPermissions() && !isLabelsManagementEnabled) {
		return <Redirect to={RoutesHelper.getUnauthorized403Page()} />;
	}

	if (isLoading) {
		return <EvSpinner srMessage={PAGE_LABELS.LOADING} />;
	}

	if (error) {
		return <EvError error={PAGE_LABELS.ERROR} />;
	}

	const existingKeys = labels.map((l) => l.key);

	return (
		<EvFlexColumn dataAutomationId={LABEL_MANAGEMENT_PAGE_DAID} padding="LARGE" gap="XLARGE">
			<LabelManagementPageHeader
				title={PAGE_HEADER_LABELS.TITLE}
				subtitle={PAGE_HEADER_LABELS.SUBTITLE}
			/>
			<LabelsTable
				labels={labels}
				onEdit={openEditPanel}
				onDelete={requestDelete}
				onCreateLabel={openAddPanel}
			/>
			{isPanelOpen && productId && (
				<LabelPanel
					key={`label-panel-${panelMode}-${activeLabel?.key ?? 'new'}`}
					activeLabel={activeLabel}
					panelMode={panelMode}
					productId={productId}
					isSaving={isSaving}
					existingKeys={existingKeys.filter((k) => k !== activeLabel?.key)}
					onSave={saveLabel}
					onClose={closePanel}
					onDelete={() => {
						if (activeLabel) {
							requestDelete(activeLabel);
						}
					}}
				/>
			)}
			<DeleteLabelConfirmModal
				label={labelToDelete}
				isDeleting={isDeleting}
				deleteError={deleteError}
				onConfirm={handleConfirmDelete}
				onCancel={cancelDelete}
			/>
		</EvFlexColumn>
	);
};
